import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { motion, useAnimation } from 'framer-motion'
import { useQueryClient } from 'react-query'
import { Link, useNavigate } from 'react-router-dom'
import styled, { css } from 'styled-components'

import avatarPlaceholder from 'assets/images/icons/user_placeholder.svg'
import { IconNotificationHeartFill } from 'assets/svg/sprite'
import { someTimeAgo } from 'lib/dayjs'
import { apiNotificationAsRead } from 'services/endpoints/notification'

interface NotificationToastProps {
  avatarSrc?: string
  isOnline?: boolean
  hasDollar?: boolean
  isSystem?: boolean
  profileUrl?: string
  name: string
  atName: string
  time: string
  notificationId: number
  children: ReactNode
  autoDismissMs?: number
  onClose?: () => void
  animateBeforeNavigate?: boolean
}

export const NotificationToast = ({
  avatarSrc,
  name,
  atName,
  time,
  children,
  isSystem,
  profileUrl,
  notificationId,
  autoDismissMs = 5000,
  onClose,
  animateBeforeNavigate = false,
}: NotificationToastProps) => {
  const [visible, setVisible] = useState(true)
  const [imageFailed, setImageFailed] = useState(false)
  const controls = useAnimation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const interacted = useRef(false)
  const onInteraction = useCallback(() => {
    if (!interacted.current && notificationId) {
      interacted.current = true
      apiNotificationAsRead(notificationId)
      queryClient.invalidateQueries('pendingStats')
    }
  }, [notificationId, queryClient])

  const onCloseRef = useRef<(() => void) | undefined>(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  const dismissing = useRef(false)
  const dismiss = useCallback(async () => {
    if (dismissing.current) return
    dismissing.current = true
    await controls.start({
      y: -220,
      opacity: 0,
      transition: { duration: 0.22, ease: 'easeOut' },
    })
    setVisible(false)
    onCloseRef.current?.()
  }, [controls])

  const timerRef = useRef<number | null>(null)
  useEffect(() => {
    if (!autoDismissMs) return
    timerRef.current = window.setTimeout(() => {
      void dismiss()
    }, autoDismissMs)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [autoDismissMs, dismiss])

  const pauseTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  const resumeTimer = () => {
    if (!autoDismissMs || timerRef.current) return
    timerRef.current = window.setTimeout(() => {
      void dismiss()
    }, autoDismissMs)
  }

  const looksLikeFullUrl = (u?: string | null) => !!u && /^https?:\/\/.+/i.test(u)

  const isPlaceholderSource =
    !avatarSrc ||
    avatarSrc.includes('/static') ||
    avatarSrc.toLowerCase().includes('placeholder') ||
    !looksLikeFullUrl(avatarSrc)

  const showPlaceholder = isPlaceholderSource || imageFailed
  const finalAvatarSrc = showPlaceholder ? avatarPlaceholder : (avatarSrc as string)

  if (!visible) return null

  const THRESHOLD = 60

  const handleDragStart = () => {
    onInteraction()
    pauseTimer()
  }

  const handleDragEnd = (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: { offset: { x: number; y: number }; velocity: { x: number; y: number } }
  ) => {
    const { y: offsetY } = info.offset
    const { y: vy } = info.velocity
    const crossedDistance = offsetY < -THRESHOLD
    const fastFlingUp = vy < -600

    if (crossedDistance || fastFlingUp) {
      void dismiss()
    } else {
      void controls
        .start({
          y: 0,
          opacity: 1,
          transition: { type: 'spring', stiffness: 520, damping: 36 },
        })
        .then(() => resumeTimer())
    }
  }

  const handleClickCapture = async (e: React.MouseEvent) => {
    const link = (e.target as HTMLElement).closest('a')
    if (!link) return

    if (!animateBeforeNavigate) {
      void dismiss()
      return
    }

    e.preventDefault()
    const href = link.getAttribute('href') ?? ''
    await dismiss()
    if (href) navigate(href)
  }

  return (
    <ContainerMotion
      layout
      onClickCapture={handleClickCapture}
      onMouseDown={onInteraction}
      onTouchStart={onInteraction}
      drag="y"
      dragDirectionLock
      dragConstraints={{ top: -Infinity, bottom: 0 }}
      dragElastic={0.2}
      style={{ y: 0, opacity: 1 }}
      animate={controls}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      whileDrag={{ opacity: 1 }}
      onMouseEnter={pauseTimer}
      onMouseLeave={resumeTimer}
    >
      {isSystem ? (
        <>
          <SystemAvatarWrap $placeholder={showPlaceholder}>
            <SystemAvatarImage
              src={finalAvatarSrc}
              alt=""
              $placeholder={showPlaceholder}
              onError={() => {
                setImageFailed(true)
              }}
            />
          </SystemAvatarWrap>

          <Content>
            <TopPart>
              <NotificationText>{name}</NotificationText>
              <Time>{someTimeAgo(time)}</Time>
            </TopPart>
            <BottomPart>{children}</BottomPart>
          </Content>
        </>
      ) : (
        <>
          <Avatar to={profileUrl ?? ''}>
            <AvatarInner $placeholder={showPlaceholder}>
              <ProfilePhoto
                src={finalAvatarSrc}
                alt="profile"
                $placeholder={showPlaceholder}
                onError={() => {
                  setImageFailed(true)
                }}
              />
            </AvatarInner>
            <Heart>
              <IconNotificationHeartFill />
            </Heart>
          </Avatar>

          <Content>
            <TopPart>
              <Name to={profileUrl ?? ''}>{name}</Name>
              <Time>{someTimeAgo(time)}</Time>
            </TopPart>
            <Handle to={profileUrl ?? ''}>@{atName}</Handle>
            <BottomPart>{children}</BottomPart>
          </Content>
        </>
      )}
    </ContainerMotion>
  )
}

const ContainerMotion = styled(motion.div)`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  padding: 20px;
  border-radius: 20px;
  background: #121418;
  opacity: 0.98;
  will-change: transform, opacity;
  touch-action: pan-y;
`

const Heart = styled.div`
  width: 1.8rem;
  height: 1.8rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  right: 0.3rem;
  bottom: 0.3rem;
  border-radius: 50%;
  background: #ffffff;
`

const Content = styled.div`
  flex: 1;
  padding-left: 1.5rem;
  overflow: hidden;
`

const Handle = styled(Link)`
  display: inline-block;
  line-height: 1.5rem;
  font-size: 1.2rem;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  letter-spacing: 0.02em;
  text-decoration: none;
  text-overflow: ellipsis;
  color: #828c94;
  overflow: hidden;
  margin-bottom: 0.2rem;
`

const TopPart = styled.div`
  display: flex;
  justify-content: space-between;
`

const MainText = css`
  line-height: 2rem;
  font-size: 1.6rem;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-decoration: none;
  text-overflow: ellipsis;
  color: #ffffff;
  overflow: hidden;
`

const Avatar = styled(Link)`
  width: 6rem;
  height: 6rem;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: 50%;
  background: linear-gradient(315deg, #8ccbff 0%, #2e97fe 100%);
  overflow: visible;
`

const AvatarInner = styled.div<{ $placeholder: boolean }>`
  width: 5.6rem;
  height: 5.6rem;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ $placeholder }) =>
    $placeholder ? 'linear-gradient(315deg, #8ccbff 0%, #2e97fe 100%)' : '#121418'};
`

const ProfilePhoto = styled.img<{ $placeholder: boolean }>`
  width: ${({ $placeholder }) => ($placeholder ? '65%' : '100%')};
  height: ${({ $placeholder }) => ($placeholder ? '65%' : '100%')};
  object-fit: ${({ $placeholder }) => ($placeholder ? 'contain' : 'cover')};
  object-position: center;
  border-radius: ${({ $placeholder }) => ($placeholder ? '0' : '50%')};
  display: block;
`

const SystemAvatarWrap = styled.div<{ $placeholder: boolean }>`
  width: 6rem;
  height: 6rem;
  min-width: 6rem;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ $placeholder }) =>
    $placeholder ? 'linear-gradient(315deg, #8ccbff 0%, #2e97fe 100%)' : '#121418'};
`

const SystemAvatarImage = styled.img<{ $placeholder: boolean }>`
  width: ${({ $placeholder }) => ($placeholder ? '65%' : '100%')};
  height: ${({ $placeholder }) => ($placeholder ? '65%' : '100%')};
  object-fit: ${({ $placeholder }) => ($placeholder ? 'contain' : 'cover')};
  object-position: center;
  border-radius: ${({ $placeholder }) => ($placeholder ? '0' : '50%')};
  display: block;
`

const Name = styled(Link)`
  ${MainText}
`

const NotificationText = styled.div`
  ${MainText}
`

const Time = styled.p`
  flex-grow: 0;
  flex-shrink: 0;
  line-height: 1.5rem;
  font-size: 1.2rem;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #828c94;
`

const BottomPart = styled.div`
  line-height: 1.7rem;
  font-size: 1.4rem;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #828c94;

  & a {
    color: #2f98fe;
    text-decoration: none;
  }
`